import React, { useState, useMemo } from 'react';
import { Form, Badge, Table } from 'reactstrap';
import { ModalButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { buildExcelWorkbook } from '../../../../../../../../../GlobalComponents/Helpers/excel';

const downloadAnalysisExcel = async (results, sourceResourceName) => {
    if (!results) return;

    const { matches, not_found, duplicates, match_field } = results;

    const sheets = [];

    // Summary sheet
    sheets.push({
        sheetName: 'Summary',
        columns: [
            { header: 'Metric', width: 30 },
            { header: 'Value', width: 20, align: 'center' },
        ],
        rows: [
            ['Total Staged Records', String(results.total_staged || 0)],
            ['Unique Values Checked', String(results.total_unique_values || 0)],
            ['Found in Destination', String(results.total_found || 0)],
            ['Not Found (New)', String(results.total_not_found || 0)],
            ['Duplicates (>1 match)', String(results.total_duplicates || 0)],
            ['API Calls Made', String(results.total_api_calls || 0)],
            ['Match Field', match_field || ''],
        ],
    });

    // Found/Matches sheet
    const matchEntries = Object.entries(matches || {}).filter(([_, records]) => records.length > 0);
    if (matchEntries.length > 0) {
        const allKeys = new Set();
        matchEntries.forEach(([_, records]) => records.forEach(r => Object.keys(r).forEach(k => allKeys.add(k))));
        const columns = [{ header: 'Match Value', width: 25 }, ...Array.from(allKeys).map(k => ({ header: k, width: 20 }))];
        const rows = [];
        matchEntries.forEach(([matchValue, records]) => {
            records.forEach(record => {
                rows.push([matchValue, ...Array.from(allKeys).map(k => {
                    const val = record[k];
                    if (val === null || val === undefined) return '';
                    if (typeof val === 'object') return JSON.stringify(val);
                    return String(val);
                })]);
            });
        });
        sheets.push({ sheetName: 'Found in Destination', columns, rows });
    }

    // Not Found sheet
    if (not_found?.length > 0) {
        const columns = [{ header: match_field, width: 35 }];
        const rows = not_found.map(v => [String(v)]);
        sheets.push({ sheetName: 'Not Found (New)', columns, rows });
    }

    // Duplicates sheet
    if (duplicates?.length > 0) {
        const columns = [{ header: match_field, width: 35 }, { header: 'Match Count', width: 15 }];
        const rows = duplicates.map(v => {
            const count = (matches[String(v)] || []).length;
            return [String(v), String(count)];
        });
        sheets.push({ sheetName: 'Duplicates', columns, rows });
    }

    await buildExcelWorkbook({
        fileName: `${sourceResourceName}_duplicate_analysis.xlsx`,
        sheets,
    });
};

const DuplicateAnalysisModal = ({
    row,
    jobData,
    jobResources,
    handleDMTAction,
    actionRegister,
    actionErrors,
    actionControl,
    actionForm,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [analysisResults, setAnalysisResults] = useState(null);

    const sourceResourceId = row.source_resource_id;
    const sourceResourceName = row.source_resource_name;

    // Look up the destination resource URI from jobResources
    const destinationResource = useMemo(() => {
        const destResources = jobResources[jobData.destination_connection.id]?.selected_resources || [];
        const found = destResources.find(r => parseInt(r.resource_id) === parseInt(row.destination_resource_id));
        return found?.resource || '';
    }, [row.destination_resource_id, jobResources, jobData]);

    // Build key options from the resource's known keys
    const availableKeys = useMemo(() => {
        // We'll let the user type the field name since we may not have keys loaded here
        return [];
    }, []);

    const resetForm = () => {
        setIsLoading(false);
        setAnalysisResults(null);
    };

    const runAnalysis = (e) => {
        e.preventDefault();
        const formData = actionControl._formValues;
        const matchField = formData['analysisMatchField_' + sourceResourceId];
        const limit = parseInt(formData['analysisLimit_' + sourceResourceId]) || 0;

        if (!matchField) return ToastBuilder('error', 'Enter a match field');

        setIsLoading(true);
        setAnalysisResults(null);

        handleDMTAction('duplicate-analysis', sourceResourceId, (results) => {
            setIsLoading(false);
            if (results?.data?.success) {
                setAnalysisResults(results.data);
                ToastBuilder('success', results.data.message);
            } else {
                ToastBuilder('error', results?.data?.message || 'Analysis failed');
            }
        }, {
            matchField,
            destinationResource,
            returnFields: ['id', matchField, 'name'].filter((v, i, a) => a.indexOf(v) === i),
            limit,
        });
    };

    const summaryBadges = analysisResults ? (
        <div className='d-flex gap-2 flex-wrap mt-3 mb-2'>
            <Badge color='secondary'>Staged: {analysisResults.total_staged}</Badge>
            <Badge color='info'>Checked: {analysisResults.total_unique_values}</Badge>
            <Badge color='success'>Found: {analysisResults.total_found}</Badge>
            <Badge color='primary'>New: {analysisResults.total_not_found}</Badge>
            {analysisResults.total_duplicates > 0 && <Badge color='warning'>Duplicates: {analysisResults.total_duplicates}</Badge>}
            <Badge color='light' className='text-dark'>API Calls: {analysisResults.total_api_calls}</Badge>
        </div>
    ) : null;

    const resultsPreview = analysisResults ? (
        <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 10 }}>
            {analysisResults.total_found > 0 && (
                <>
                    <h6 className='text-success mt-2'>✅ Found in Destination ({analysisResults.total_found})</h6>
                    <Table size='sm' bordered striped style={{ fontSize: 12 }}>
                        <thead>
                            <tr>
                                <th>{analysisResults.match_field}</th>
                                <th>Destination ID</th>
                                <th>Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(analysisResults.matches || {})
                                .filter(([_, records]) => records.length > 0)
                                .slice(0, 50)
                                .map(([matchValue, records], i) => (
                                    records.map((r, j) => (
                                        <tr key={`${i}-${j}`} className={records.length > 1 ? 'table-warning' : ''}>
                                            <td>{matchValue} {records.length > 1 && <Badge color='warning' style={{ fontSize: 9 }}>DUP</Badge>}</td>
                                            <td>{r.id}</td>
                                            <td>{r.name || 'N/A'}</td>
                                        </tr>
                                    ))
                                ))}
                        </tbody>
                    </Table>
                </>
            )}

            {analysisResults.total_found === 0 && analysisResults.total_not_found > 0 && (
                <p className='text-primary mt-3' style={{ fontSize: 13 }}>
                    🆕 No matches found — all {analysisResults.total_not_found} records are new to the destination.
                </p>
            )}
        </div>
    ) : null;

    return (
        <ModalButtonBuilder
            title='Duplicate Check'
            className='btn-outline-warning'
            formId={'duplicate-analysis-form-' + sourceResourceId}
            modalTitle={`Duplicate Analysis: ${sourceResourceName}`}
            size='lg'
            onClick={resetForm}
            body={
                <Form id={'duplicate-analysis-form-' + sourceResourceId} onSubmit={runAnalysis}>
                    <p>Check if staged <strong>{sourceResourceName}</strong> records already exist in the destination before migrating.</p>
                    <ul style={{ fontSize: 12, color: '#636E72' }}>
                        <li>Enter the field that identifies records uniquely (e.g. <code>identifier</code>, <code>name</code>)</li>
                        <li>The system extracts that field from all staged records and queries the destination</li>
                        <li>Results show which records would be duplicates if migrated</li>
                    </ul>
                    <hr />
                    <RegisteredInputsBuilder
                        registrator={actionRegister}
                        registratorErrors={actionErrors}
                        control={actionControl}
                        inputs={[
                            {
                                label: 'Match Field',
                                labelSize: 3,
                                type: 'text',
                                name: 'analysisMatchField_' + sourceResourceId,
                                placeholder: 'e.g. identifier, name, email',
                                required: true,
                            },
                            {
                                label: 'Limit',
                                labelSize: 3,
                                type: 'number',
                                name: 'analysisLimit_' + sourceResourceId,
                                placeholder: '0 = all staged records',
                                helpText: 'Limit how many staged records to check (0 for all)',
                            },
                        ]}
                    />

                    {isLoading && <p style={{ color: '#636E72', fontSize: 12, marginTop: 10 }}>Analyzing records against destination... This may take a moment for large datasets.</p>}

                    {summaryBadges}
                    {resultsPreview}

                    {analysisResults && (
                        <div className='mt-3'>
                            <ButtonBuilder
                                label='Export to Excel'
                                className='btn-success btn-sm'
                                onClick={() => downloadAnalysisExcel(analysisResults, sourceResourceName)}
                            />
                        </div>
                    )}
                </Form>
            }
        />
    );
};

export default DuplicateAnalysisModal;
